/*
 * Copyright (c) Microsoft.
 */

/* global document, Excel, Office */

import { FileUploadManager } from "../services/file-upload-manager";
import { TabManager } from "../shared/ui/tab-manager";
import { ReportUIManager } from "../services/report-ui-manager";
import { ClientCarrierController } from "../services/client-carrier-controller";
import { reportDataCall, clientMappingSubmitCall, clientMappingGetCall, clientListDataCall, getErrorDataCall } from "../core/api/report-api";
import { showLoading, hideLoading } from "../shared/ui/load-modal";
import { getClientMap, clearClientMap } from "../services/client-data-manager";
import { Config } from "../services/config";
import { checkRebateStatus, setRebateNotRequired } from "../core/excel/rebate-check";

// Main client list global (kept if referenced elsewhere)
var clientList: any = null;

/* ---------- Version Display ---------- */
async function displayManifestVersion() {
  try {
    // Fetch the manifest.xml file
    const response = await fetch('/manifest_prod.xml');
    if (!response.ok) {
      throw new Error(`Failed to fetch manifest: ${response.status}`);
    }

    const manifestText = await response.text();

    // Parse the XML to extract the version
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(manifestText, 'text/xml');

    // Get the Version element
    const versionElement = xmlDoc.getElementsByTagName('Version')[0];
    if (versionElement && versionElement.textContent) {
      const versionDisplay = document.getElementById('version-display');
      if (versionDisplay) {
        versionDisplay.textContent = `Ver. ${versionElement.textContent}`;
      }
    }
  } catch (error) {
    console.error('Error loading manifest version:', error);
    // Silently fail - version display will just be empty
  }
}

/* ---------- Shared state ---------- */
let _clientUiInited = false;

/* ---------- Public init that sets up both tabs ---------- */
export async function initClientCarrierUI() {
  if (_clientUiInited) return;
  _clientUiInited = true;

  const uploadCtrl = new ClientCarrierController({
    searchId: "clientSearch",
    clientId: "uploadClientSelect",
    carrierId: "uploadCarrierSelect",
  }, true); // Only show clients with hasMapping = true

  const reportsCtrl = new ClientCarrierController({
    searchId: "clientSearchTab1",
    clientId: "clientSelect",
    carrierId: "carrierSelect",
  }, true); // Only show clients with hasMapping = true

  const mappingCtrl = new ClientCarrierController({
    searchId: "clientSearchMapping",
    clientId: "clientSelectMapping",
    carrierId: "carrierSelectMapping",
  }, null); // Show all clients regardless of hasMapping

  await Promise.all([uploadCtrl.init(), reportsCtrl.init(), mappingCtrl.init()]);
}

/* ---------- Refresh client data after mapping changes ---------- */
export async function refreshClientData() {
  try {
    // Clear the cached client data
    clearClientMap();
    
    // Reinitialize all client/carrier controllers with fresh data
    const uploadCtrl = new ClientCarrierController({
      searchId: "clientSearch",
      clientId: "uploadClientSelect",
      carrierId: "uploadCarrierSelect",
    }, true); // Only show clients with hasMapping = true

    const reportsCtrl = new ClientCarrierController({
      searchId: "clientSearchTab1",
      clientId: "clientSelect",
      carrierId: "carrierSelect",
    }, true); // Only show clients with hasMapping = true

    const mappingCtrl = new ClientCarrierController({
      searchId: "clientSearchMapping",
      clientId: "clientSelectMapping",
      carrierId: "carrierSelectMapping",
    }, null); // Show all clients regardless of hasMapping

    await Promise.all([uploadCtrl.init(), reportsCtrl.init(), mappingCtrl.init()]);
  } catch (error) {
    console.error("Error refreshing client data:", error);
    throw error;
  }
}

/* ---------- File Extensions Initialization ---------- */
function initializeFileExtensions() {
  const dropZoneHelp = document.getElementById("dropZoneHelp");
  const fileInput = document.getElementById("fileInput") as HTMLInputElement;
  
  if (dropZoneHelp) {
    dropZoneHelp.textContent = `Drop ${Config.UPLOAD.getExtensionsForDisplay()} files here, or click to browse.`;
  }
  
  if (fileInput) {
    fileInput.accept = Config.UPLOAD.getExtensionsForAccept();
  }
}

/* ---------- App wiring ---------- */
Office.onReady((info) => {
  if (info.host !== Office.HostType.Excel) return;

  // Display manifest version
  displayManifestVersion();

  // Initialize file extensions from config
  initializeFileExtensions();

  // Initialize UI managers
  const tabManager = new TabManager();
  const reportUIManager = new ReportUIManager();
  const fileUploadManager = new FileUploadManager();
  fileUploadManager.init();
  
  // Set up report handler
  reportUIManager.setRunReportHandler(run);

  // Initialize both client/carrier controllers (Upload + Reports tabs)
  initClientCarrierUI();

  // Set up mapping toggle functionality
  setupMappingToggle();
  
  // Set up submit mapping functionality
  setupSubmitMapping();
  
  // Set up mapping search functionality
  setupMappingSearch();
  
  // Set up clear mapping functionality
  setupClearMapping();
  
  // Set up get error data functionality
  setupGetErrorData();
});

// Main run button handler
export async function run() {
  //await initClientCarrierUI();
  //return false;

  const clientSelect = document.querySelector<HTMLSelectElement>("#clientSelect")!;
  const clientSelectVal = clientSelect.value;

  const monthSelect = document.querySelector<HTMLSelectElement>("#monthSelect")!;
  const monthSelectVal = monthSelect.value;

  const runDataSelect = document.querySelector<HTMLSelectElement>("#runDataSelect")!;
  const runDataSelectVal = runDataSelect.value;

  const messageArea = document.getElementById("message-area") as HTMLElement;

  try {
    // Clear any previous messages
    if (messageArea) {
      messageArea.textContent = "Running report...";
      messageArea.style.color = "#0078d4";
    }

        // Run rebate check before report
    const rebateCheckResult = await checkRebateStatus(monthSelectVal);
    
    if (!rebateCheckResult.shouldContinue) {
      // Show rebate modal and wait for user response
      const userResponse = await showRebateModal();
      
      if (userResponse === "no") {
        // User said rebates are not required, set the flag
        await setRebateNotRequired();
      }
      // If user said "yes", just continue with the report
    }
    await reportDataCall(clientSelectVal, monthSelectVal, runDataSelectVal);

    // Show success message
    if (messageArea) {
      messageArea.textContent = "Report completed successfully!";
      messageArea.style.color = "#107c10"; // Green for success
    }
  } catch (error) {
    console.error("Error running report:", error);
    
    // Show error message to user
    if (messageArea) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      messageArea.textContent = `Error: ${errorMessage}`;
      messageArea.style.color = "#a80000"; // Red for error
    }
  }
}

// Helper function to show rebate modal and wait for user response
function showRebateModal(): Promise<"yes" | "no"> {
  return new Promise((resolve) => {
    const modal = document.getElementById("rebateModal") as HTMLElement;
    const yesBtn = document.getElementById("rebateYesBtn") as HTMLButtonElement;
    const noBtn = document.getElementById("rebateNoBtn") as HTMLButtonElement;
    
    if (!modal || !yesBtn || !noBtn) {
      console.error("Rebate modal elements not found");
      resolve("yes"); // Default to continue if modal not found
      return;
    }
    
    // Show modal
    modal.setAttribute("data-open", "true");
    modal.setAttribute("aria-hidden", "false");
    
    // Set up button handlers
    const handleYes = () => {
      modal.setAttribute("data-open", "false");
      modal.setAttribute("aria-hidden", "true");
      yesBtn.removeEventListener("click", handleYes);
      noBtn.removeEventListener("click", handleNo);
      resolve("yes");
    };
    
    const handleNo = () => {
      modal.setAttribute("data-open", "false");
      modal.setAttribute("aria-hidden", "true");
      yesBtn.removeEventListener("click", handleYes);
      noBtn.removeEventListener("click", handleNo);
      resolve("no");
    };
    
    yesBtn.addEventListener("click", handleYes);
    noBtn.addEventListener("click", handleNo);
  });
}

/* ---------- Mapping Toggle Functionality ---------- */
function setupMappingToggle() {
  const toggleBtn = document.getElementById("toggleMappingBtn");
  const mappingSection = document.getElementById("mappingAddSection");
  
  if (toggleBtn && mappingSection) {
    toggleBtn.addEventListener("click", () => {
      mappingSection.classList.toggle("hidden");
    });
  }
}

/* ---------- Submit Mapping Functionality ---------- */
function setupSubmitMapping() {
  const submitBtn = document.getElementById("submitMappingBtn");
  
  if (submitBtn) {
    submitBtn.addEventListener("click", async () => {
      const clientSelect = document.querySelector<HTMLSelectElement>("#clientSelectMapping")!;
      const carrierSelect = document.querySelector<HTMLSelectElement>("#carrierSelectMapping")!;
      const carrierPlanInput = document.querySelector<HTMLInputElement>("#clientPlanMappingCarrier")!;
      const mbPlanInput = document.querySelector<HTMLInputElement>("#clientPlanMappingMB")!;
      const messageEl = document.getElementById("mappingMessage") as HTMLElement;
      
      const clientId = clientSelect.value;
      const carrierSelectMapping = carrierSelect.value;
      const carrierPlan = carrierPlanInput.value.trim();
      const mbPlan = mbPlanInput.value.trim();
      
      // Clear any previous messages
      if (messageEl) {
        messageEl.textContent = "";
        messageEl.style.color = "";
      }
      
      // Validation
      if (!clientId || clientId === "0") {
        if (messageEl) {
          messageEl.textContent = "Please select a client.";
          messageEl.style.color = "#a80000";
        }
        return;
      }
      
      if (!carrierPlan) {
        if (messageEl) {
          messageEl.textContent = "Please enter a carrier plan.";
          messageEl.style.color = "#a80000";
        }
        return;
      }
      
      if (!mbPlan) {
        if (messageEl) {
          messageEl.textContent = "Please enter an MB plan.";
          messageEl.style.color = "#a80000";
        }
        return;
      }
      
      try {
        // Show loading modal with "Adding Mapping..." text
        showLoading({ text: "Adding Mapping..." });
        
        // Get the mapping ID if it's an edit operation
        const mappingIdInput = document.querySelector<HTMLInputElement>("#mappingId")!;
        const mappingId = mappingIdInput ? mappingIdInput.value : "";
        
        // Submit the mapping (with ID if editing)
        const result = await clientMappingSubmitCall(clientId, carrierPlan, mbPlan, carrierSelectMapping, mappingId);
        
        // Handle response based on status
        if (result && result.status === 1) {
          // Success - clear form and show success message
          if (messageEl) {
            messageEl.textContent = mappingId ? "Mapping updated successfully!" : "Mapping added successfully!";
            messageEl.style.color = "#107c10"; // Green for success
          }
          
          // Clear the form
          carrierPlanInput.value = "";
          mbPlanInput.value = "";
          clientSelect.value = "0";
          carrierSelect.value = "0";
          if (mappingIdInput) mappingIdInput.value = "";
          
          // Clear the search textbox
          const searchInput = document.querySelector<HTMLInputElement>("#clientSearchMapping")!;
          if (searchInput) {
            searchInput.value = "";
          }
          
          // Refresh client data to update all UI components
          try {
            await refreshClientData();
          } catch (error) {
            console.error("Error refreshing client data after mapping operation:", error);
          }
          
          performMappingSearch();
        } else {
          // Error - show error message
          const errorMessage = result?.message || "Unknown error occurred";
          if (messageEl) {
            messageEl.textContent = `Error: ${errorMessage}`;
            messageEl.style.color = "#a80000"; // Red for error
          }
        }
      } catch (error) {
        console.error("Error submitting mapping:", error);
        if (messageEl) {
          messageEl.textContent = "Error adding mapping. Please try again.";
          messageEl.style.color = "#a80000";
        }
      } finally {
        // Hide loading modal
        hideLoading();
      }
    });
  }
}

/* ---------- Clear Mapping Functionality ---------- */
function setupClearMapping() {
  const clearBtn = document.getElementById("clearMappingBtn");
  
  if (clearBtn) {
    clearBtn.addEventListener("click", () => {
      // Clear all form fields
      const clientSelect = document.querySelector<HTMLSelectElement>("#clientSelectMapping")!;
      const carrierSelect = document.querySelector<HTMLSelectElement>("#carrierSelectMapping")!;
      const carrierPlanInput = document.querySelector<HTMLInputElement>("#clientPlanMappingCarrier")!;
      const mbPlanInput = document.querySelector<HTMLInputElement>("#clientPlanMappingMB")!;
      const mappingIdInput = document.querySelector<HTMLInputElement>("#mappingId")!;
      const searchInput = document.querySelector<HTMLInputElement>("#clientSearchMapping")!;
      const messageEl = document.getElementById("mappingMessage") as HTMLElement;
      
      // Reset form values
      if (clientSelect) clientSelect.value = "0";
      if (carrierSelect) carrierSelect.value = "0";
      if (carrierPlanInput) carrierPlanInput.value = "";
      if (mbPlanInput) mbPlanInput.value = "";
      if (mappingIdInput) mappingIdInput.value = "";
      if (searchInput) searchInput.value = "";
      
      // Clear any messages
      if (messageEl) {
        messageEl.textContent = "";
        messageEl.style.color = "";
      }
    });
  }
}

/* ---------- Mapping Search Functionality ---------- */
function setupMappingSearch() {
  const searchBtn = document.getElementById("mappingSearchBtn");
  const searchInput = document.querySelector<HTMLInputElement>("#mappingSearchInput")!;
  
  if (searchBtn && searchInput) {
    // Search on button click
    searchBtn.addEventListener("click", performMappingSearch);
    
    // Search on Enter key
    searchInput.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        performMappingSearch();
      }
    });
  }
}

async function performMappingSearch() {
  const searchInput = document.querySelector<HTMLInputElement>("#mappingSearchInput")!;
  const messageEl = document.getElementById("mappingSearchMessage") as HTMLElement;
  const resultsSection = document.getElementById("mappingResultsSection")!;
  const resultsList = document.getElementById("mappingResultsList")!;
  
  const searchTerm = searchInput.value.trim();
  
  // Clear any previous messages and results
  if (messageEl) {
    messageEl.textContent = "";
    messageEl.style.color = "";
  }
  resultsList.innerHTML = "";
  resultsSection.style.display = "none";
  
  try {
    // Show loading modal
    showLoading({ text: "Searching..." });

    const result = await clientMappingGetCall(searchTerm);
    
    // Handle response based on status
    if (result && result.status === 1 && result.data) {
      // Success - display results
      // The API returns data as List<Dictionary<string, object>>, so we need to handle it properly
      const dataArray = Array.isArray(result.data) ? result.data : [result.data];
      displayMappingResults(dataArray);
      
      if (messageEl) {
        messageEl.textContent = `Found ${dataArray.length} result(s)`;
        messageEl.style.color = "#107c10"; // Green for success
      }
    } else {
      // No results or error
      const errorMessage = result?.message || "No results found";
      if (messageEl) {
        messageEl.textContent = errorMessage;
        messageEl.style.color = "#a80000"; // Red for error
      }
    }
  } catch (error) {
    console.error("Error searching mappings:", error);
    if (messageEl) {
      messageEl.textContent = "Error searching mappings. Please try again.";
      messageEl.style.color = "#a80000";
    }
  } finally {
    // Hide loading modal
    hideLoading();
  }
}

function displayMappingResults(data: any[]) {
  const resultsSection = document.getElementById("mappingResultsSection")!;
  const resultsList = document.getElementById("mappingResultsList")!;
  
  // Clear previous results
  resultsList.innerHTML = "";
  
  if (!data || data.length === 0) {
    return;
  }
  
  // Create result items
  data.forEach((item: any) => {
    const li = document.createElement("li");
    
    const contentDiv = document.createElement("div");
    contentDiv.className = "mapping-result-content";
    
    const infoDiv = document.createElement("div");
    infoDiv.className = "mapping-result-info";
    infoDiv.innerHTML = `
      <strong>Client ID:</strong> ${item.clientId || 'N/A'}<br>
      <strong>Carrier:</strong> ${item.carrier || 'N/A'}<br>
      <strong>Carrier Plan:</strong> ${item.carrierMapping || 'N/A'}<br>
      <strong>MB Plan:</strong> ${item.mbMapping || 'N/A'}
    `;
    
    const actionsDiv = document.createElement("div");
    actionsDiv.className = "mapping-result-actions";
    
    const editBtn = document.createElement("button");
    editBtn.className = "edit-btn";
    editBtn.textContent = "Edit";
    editBtn.onclick = () => editMapping(item);
    
    const deleteBtn = document.createElement("button");
    deleteBtn.className = "delete-btn";
    deleteBtn.textContent = "Delete";
    deleteBtn.onclick = () => confirmDeleteMapping(item);
    
    actionsDiv.appendChild(editBtn);
    actionsDiv.appendChild(deleteBtn);
    
    contentDiv.appendChild(infoDiv);
    contentDiv.appendChild(actionsDiv);
    
    li.appendChild(contentDiv);
    resultsList.appendChild(li);
  });
  
  // Show results section
  resultsSection.style.display = "block";
}

function editMapping(item: any) {
  // Show the add mapping section if it's hidden
  const mappingSection = document.getElementById("mappingAddSection")!;
  const toggleBtn = document.getElementById("toggleMappingBtn")!;
  
  if (mappingSection.classList.contains("hidden")) {
    mappingSection.classList.remove("hidden");
  }
  
  // Populate the form with the mapping data
  const clientSelect = document.querySelector<HTMLSelectElement>("#clientSelectMapping")!;
  const carrierSelect = document.querySelector<HTMLSelectElement>("#carrierSelectMapping")!;
  const carrierPlanInput = document.querySelector<HTMLInputElement>("#clientPlanMappingCarrier")!;
  const mbPlanInput = document.querySelector<HTMLInputElement>("#clientPlanMappingMB")!;
  const mappingIdInput = document.querySelector<HTMLInputElement>("#mappingId")!;
  
  // Set the non-client values
  if (carrierPlanInput) carrierPlanInput.value = item.carrierMapping || "";
  if (mbPlanInput) mbPlanInput.value = item.mbMapping || "";
  if (mappingIdInput) mappingIdInput.value = item.id || "";
  if (carrierSelect) carrierSelect.value = item.CarrierSelectMapping || "0";
  
  // Find the client using normalized comparison
  const clientMap = getClientMap();
  const targetClientId = (item.clientId || "").toLowerCase().trim();
  
  let foundClient = null;
  if (targetClientId) {
    // Search through all client keys using the same normalization as the API
    for (const clientKey of Object.keys(clientMap)) {
      if (clientKey.toLowerCase().trim() === targetClientId) {
        foundClient = clientKey;
        break;
      }
    }
  }
  
  // Set the client selection
  if (foundClient && clientSelect) {
    clientSelect.value = foundClient;
    // Trigger change event to update carrier selection
    clientSelect.dispatchEvent(new Event("change", { bubbles: true }));
  } else if (clientSelect) {
    clientSelect.value = "0";
  }
  
  // Scroll to the add mapping section
  mappingSection.scrollIntoView({ behavior: 'smooth' });
}

function confirmDeleteMapping(item: any) {
  console.log("confirmDeleteMapping called with item:", item);
  
  // Create custom confirmation dialog since window.confirm is not supported in Office Add-ins
  const dialog = document.createElement('div');
  dialog.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;
  
  const dialogBox = document.createElement('div');
  dialogBox.style.cssText = `
    background: white;
    padding: 20px;
    border-radius: 8px;
    max-width: 400px;
    text-align: center;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  `;
  
  const message = document.createElement('p');
  message.innerHTML = `Are you sure you want to delete this mapping?<br><br>
    <strong>Client ID:</strong> ${item.clientId || 'N/A'}<br>
    <strong>Carrier:</strong> ${item.CarrierSelectMapping || 'N/A'}<br>
    <strong>Carrier Plan:</strong> ${item.carrierMapping || 'N/A'}<br>
    <strong>MB Plan:</strong> ${item.mbMapping || 'N/A'}`;
  message.style.marginBottom = '20px';
  
  const buttonContainer = document.createElement('div');
  buttonContainer.style.cssText = 'display: flex; gap: 10px; justify-content: center;';
  
  const confirmBtn = document.createElement('button');
  confirmBtn.textContent = 'Delete';
  confirmBtn.style.cssText = `
    background: #a800005d;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
  `;
  
  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = 'Cancel';
  cancelBtn.style.cssText = `
    background: #0078d431;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
  `;
  
  buttonContainer.appendChild(confirmBtn);
  buttonContainer.appendChild(cancelBtn);
  dialogBox.appendChild(message);
  dialogBox.appendChild(buttonContainer);
  dialog.appendChild(dialogBox);
  document.body.appendChild(dialog);
  
  confirmBtn.onclick = () => {
    console.log("User confirmed delete, calling deleteMapping");
    document.body.removeChild(dialog);
    deleteMapping(item);
  };
  
  cancelBtn.onclick = () => {
    console.log("User cancelled delete");
    document.body.removeChild(dialog);
  };
  
  // Close dialog if clicking outside
  dialog.onclick = (e) => {
    if (e.target === dialog) {
      console.log("User cancelled delete (clicked outside)");
      document.body.removeChild(dialog);
    }
  };
}

async function deleteMapping(item: any) {
  console.log("deleteMapping function called with item:", item);
  const messageEl = document.getElementById("mappingSearchMessage") as HTMLElement;
  
  try {
    console.log("About to show loading modal for delete");
    // Show loading modal
    showLoading({ text: "Deleting Mapping..." });
    
    // Delete the mapping by calling submit API with delete action
    const result = await clientMappingSubmitCall(
      item.clientId || "",
      item.carrierMapping || "",
      item.mbMapping || "",
      item.CarrierSelectMapping,
      item.id,
      "delete"
    );
    
    // Handle response based on status
    if (result && result.status === 1) {
      // Success - show success message and refresh search
      if (messageEl) {
        messageEl.textContent = "Mapping deleted successfully!";
        messageEl.style.color = "#107c10"; // Green for success
      }

      // Refresh client data to update all UI components
      try {
        await refreshClientData();
      } catch (error) {
        console.error("Error refreshing client data after mapping deletion:", error);
      }

      performMappingSearch();

    } else {
      // Error - show error message
      const errorMessage = result?.message || "Unknown error occurred";
      if (messageEl) {
        messageEl.textContent = `Error: ${errorMessage}`;
        messageEl.style.color = "#a80000"; // Red for error
      }
    }
  } catch (error) {
    console.error("Error deleting mapping:", error);
    if (messageEl) {
      messageEl.textContent = "Error deleting mapping. Please try again.";
      messageEl.style.color = "#a80000";
    }
  } finally {
    // Hide loading modal
    hideLoading();
  }
}

/* ---------- Get Error Data Functionality ---------- */
function setupGetErrorData() {
  const getErrorDataBtn = document.getElementById("getErrorDataBtn");
  
  if (getErrorDataBtn) {
    getErrorDataBtn.addEventListener("click", async () => {
      const messageArea = document.getElementById("message-area-error") as HTMLElement;
      const errorContainer = document.getElementById("errorDataContainer") as HTMLElement;
      const errorList = document.getElementById("errorDataList") as HTMLElement;
      
      try {
        // Show loading state
        if (messageArea) {
          messageArea.textContent = "Getting error data...";
          messageArea.style.color = "#0078d4";
        }
        
        // Hide previous results
        if (errorContainer) {
          errorContainer.style.display = "none";
        }
        
        // Call the API
        const result = await getErrorDataCall();
        
        // Show success message
        if (messageArea) {
          messageArea.textContent = "Error data retrieved successfully!";
          messageArea.style.color = "#107c10";
        }
        
        // Display the error data
        if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
          displayErrorData(result.data, errorList);
          if (errorContainer) {
            errorContainer.style.display = "block";
          }
        } else {
          if (errorList) {
            errorList.innerHTML = "<p class='ms-font-s' style='color: #666;'>No error data found.</p>";
          }
          if (errorContainer) {
            errorContainer.style.display = "block";
          }
        }
        
        console.log("Error data result:", result);
        
      } catch (error) {
        console.error("Error getting error data:", error);
        
        // Show error message
        if (messageArea) {
          messageArea.textContent = "Error retrieving error data. Please try again.";
          messageArea.style.color = "#a80000";
        }
        
        // Hide error container on error
        if (errorContainer) {
          errorContainer.style.display = "none";
        }
      }
    });
  }
}

function displayErrorData(errorData: any[], errorList: HTMLElement | null) {
  if (!errorList) return;
  
  // Group errors by client for better organization
  const groupedErrors = errorData.reduce((groups, error) => {
    const client = error.client || 'Unknown Client';
    if (!groups[client]) {
      groups[client] = [];
    }
    groups[client].push(error);
    return groups;
  }, {} as Record<string, any[]>);
  
  let html = '';
  
  // Display each client's errors
  Object.entries(groupedErrors).forEach(([client, errors]) => {
    html += `
      <div class="error-client-group">
        <h4 class="ms-font-s error-client-title">${escapeHtml(client)}</h4>
        <div class="error-items">
    `;
    
    (errors as any[]).forEach(error => {
      html += `
        <div class="error-item">
          <div class="error-header">
            <span class="error-date">${escapeHtml(error.errorDate || 'Unknown Date')}</span>
            <span class="error-file">${escapeHtml(error.File || 'Unknown File')}</span>
          </div>
          <div class="error-carrier">Carrier: ${escapeHtml(error.carrier || 'Unknown')}</div>
          <div class="error-message">${escapeHtml(error.errorMessage || 'No error message')}</div>
        </div>
      `;
    });
    
    html += `
        </div>
      </div>
    `;
  });
  
  errorList.innerHTML = html;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/* ---------- Fallback init for non-Office contexts ---------- */
if ((window as any).Office?.onReady) {
  (window as any).Office.onReady(() => initClientCarrierUI());
} else {
  document.addEventListener("DOMContentLoaded", () => initClientCarrierUI());
}